import React, { useLayoutEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { t } from 'src/translations'
import {
  AdvancedMarker as GoogleMapsMarker,
  InfoWindow,
  Pin,
  useAdvancedMarkerRef
} from '@vis.gl/react-google-maps'

import {
  DiningOptionBehavior,
  RestaurantLocationsQuery,
  useOoAvailabilityQuery
} from 'src/apollo/onlineOrdering'

import Image from 'shared/components/common/Image'
import Button from 'shared/components/common/button'
import { ToggleInput } from 'shared/components/common/forms'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'

import { getDiningOptionBehaviorAvailability } from 'public/components/online_ordering/FulfillmentContext'
import { formatLocationAddress } from 'public/components/online_ordering/addressUtils'
import LocationCta, {
  CtaDestination
} from 'public/components/pages/location_selection_page/LocationCta'

export type OORestaurant = NonNullable<
  NonNullable<RestaurantLocationsQuery['restaurants']>[0]
> & { __typename: 'Restaurant' }

type Props = {
  useOrderFulfillment?: boolean
  position: google.maps.LatLng | google.maps.LatLngLiteral
  restaurant: OORestaurant
  onClick: () => void
  onClose?: () => void
  onSelect: (guid: string, diningOptionBehavior?: DiningOptionBehavior) => void
  isInfoOpen?: boolean
  isSelected?: boolean
  selectedDiningOption?: DiningOptionBehavior
  shortUrl: string
  orderPathPattern: string | null
  hostName: string | null
  locationName?: string
}

export const LocationSelection = ({
  restaurant,
  onSelect
}: {
  restaurant: OORestaurant
  onSelect: (guid: string, diningOptionBehavior?: DiningOptionBehavior) => void
}) => {
  return (
    <div className='mapInfoWindow'>
      <div>{formatLocationAddress(restaurant.location, false)}</div>
      <br />
      <Button
        className='orderHereButton'
        onClick={() => onSelect(restaurant.guid, undefined)}
      >
        {t('fulfillment-marker.select-location')}
      </Button>
    </div>
  )
}

export const OrderFulfillment = ({
  restaurant,
  selectedDiningOption,
  shortUrl,
  orderPathPattern,
  hostName
}: {
  restaurant: OORestaurant
  selectedDiningOption?: DiningOptionBehavior
  shortUrl: string
  orderPathPattern: string
  hostName: string
}) => {
  // This grabs the dining options for the location represented by the map pin that was clicked on. This means
  // FulfillmentContext can't be used here because that represents the location that the user is currently navigated to.
  const client = useOOClient()
  const { data, loading } = useOoAvailabilityQuery({
    variables: { restaurantGuid: restaurant.guid },
    client,
    ssr: false
  })

  const { takeoutEnabled, deliveryEnabled, canOrderTakeout, canOrderDelivery } =
    getDiningOptionBehaviorAvailability(data?.diningOptions)

  const [selected, setSelected] = useState<DiningOptionBehavior | undefined>(
    selectedDiningOption
  )

  return (
    <div className='mapInfoWindow'>
      <div>{formatLocationAddress(restaurant.location, false)}</div>
      <hr />
      <div className='options'>
        {loading ? (
          <Skeleton width='100%' height='20px' />
        ) : (
          <>
            {!canOrderTakeout && !canOrderDelivery && (
              <div>{t('fulfillment-marker.we-are-not')}</div>
            )}
            {(takeoutEnabled || deliveryEnabled) && (
              <fieldset>
                <legend>{t('fulfillment-marker.select')}</legend>
                {takeoutEnabled && (
                  <ToggleInput
                    onChange={() => setSelected(DiningOptionBehavior.TakeOut)}
                    id={`${restaurant.guid}_pickup`}
                    name={restaurant.guid}
                    type='radio'
                    disabled={!canOrderTakeout}
                    checked={selected === DiningOptionBehavior.TakeOut}
                  >
                    <div className='itemLabel'>
                      <span>{t('fulfillment-marker.pickup')}</span>
                      <Image alt='' src='icons/pickup.svg' />
                    </div>
                  </ToggleInput>
                )}
                {deliveryEnabled && (
                  <ToggleInput
                    onChange={() => setSelected(DiningOptionBehavior.Delivery)}
                    id={`${restaurant.guid}_delivery`}
                    name={restaurant.guid}
                    type='radio'
                    disabled={!canOrderDelivery}
                    checked={selected === DiningOptionBehavior.Delivery}
                  >
                    <div className='itemLabel'>
                      <span>{t('fulfillment-marker.delivery')}</span>
                      <Image alt='' src='icons/delivery.svg' />
                    </div>
                  </ToggleInput>
                )}
              </fieldset>
            )}
          </>
        )}
      </div>
      <div>
        <LocationCta
          className='fulfillmentCta'
          disabled={
            loading || (!selected && (canOrderTakeout || canOrderDelivery))
          }
          shortUrl={shortUrl}
          rxName={restaurant.name}
          ctaDestination={
            canOrderTakeout || canOrderDelivery
              ? CtaDestination.OrderPage
              : CtaDestination.MenuPage
          }
          orderPathPattern={orderPathPattern}
          hostName={hostName}
          diningOptionBehavior={selected}
          autoFocus
        />
      </div>
    </div>
  )
}

const Marker = (props: Props) => {
  const [markerRef, marker] = useAdvancedMarkerRef()

  useLayoutEffect(() => {
    if (!marker) return

    marker.element.setAttribute('aria-haspopup', 'dialog')
  }, [marker])

  return (
    <>
      <GoogleMapsMarker
        ref={markerRef}
        key={props.restaurant.guid}
        position={props.position}
        onClick={props.onClick}
        title={t('fulfillment-marker.view-location-details', {
          location: props.restaurant.name
        })}
      >
        <Pin />
      </GoogleMapsMarker>
      {props.isInfoOpen ? (
        <InfoWindow
          anchor={marker}
          onClose={props.onClose}
          shouldFocus={true}
          headerContent={
            <h3
              style={{
                wordWrap: 'break-word',
                maxWidth: '136px',
                color: '#252525'
              }}
            >
              {props.locationName ||
                props.restaurant.whiteLabelName ||
                props.restaurant.name}
            </h3>
          }
        >
          {props.useOrderFulfillment &&
          props.orderPathPattern &&
          props.hostName ? (
            <OrderFulfillment
              orderPathPattern={props.orderPathPattern}
              hostName={props.hostName}
              shortUrl={props.shortUrl}
              restaurant={props.restaurant}
              selectedDiningOption={props.selectedDiningOption}
            />
          ) : (
            <LocationSelection
              restaurant={props.restaurant}
              onSelect={props.onSelect}
            />
          )}
        </InfoWindow>
      ) : null}
    </>
  )
}

export default Marker
